import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import Utils from "Utils/utils";
import InputTextBox from "Components/common/InputTextBox";
import Button from "Components/common/Button";
import CustomTooltip from "Components/common/CustomTooltip";
import { toasterState } from "Components/common/toaster";
import { accountsState } from "./AccountsState";

const CreateNewList = observer((props = {}) => {
  const {
    listName = "",
    loader = false,
    err = "",
    showCreateList = false,
    setShowCreateList = () => {},
    changeAction = () => {},
    setListName = () => {},
    action = () => {}
  } = props || {};

  const toggleListCreation = () => {
    setShowCreateList(!showCreateList);
    showCreateList && setListName("");
  };

  const createListInputObj = {
    name: "createNewAccountList",
    id: "createNewAccountList",
    type: "text",
    value: listName,
    change: changeAction,
    err,
    placeHolder: "Enter the list name",
    focus: true,
    autoFocus: true,
    cbk: event => (Utils.isSubmitKeyAction(event) && action()) || {}
  };

  const createListBtn = {
    id: "addNewAccountListBtn",
    name: "addNewAccountListBtn",
    btnText: "",
    type: "button",
    loader,
    icon: true,
    iconName: "playlist_add",
    className: `addNewListBtn ${listName?.trim()?.length ? "" : "disabled"} ${
      loader ? "loading" : ""
    }`,
    click: action,
    iconCbk: () => {}
  };

  return (
    <div className="createNewListButton">
      <div className="createListBtn" onClick={toggleListCreation}>
        <i className="material-icons-outlined add">control_point</i>
        <span className="text">Create a new list</span>
      </div>
      {showCreateList && (
        <div className="wrapInputAction">
          <InputTextBox {...createListInputObj} />
          <Button {...createListBtn} />
        </div>
      )}
    </div>
  );
});

export const AddAccountsToList = observer((props = {}) => {
  const {
    accountIds = [],
    loading = false,
    onAddToList = () => {},
    hidden = false,
    trigger = 0
  } = props || {};

  const [showCreateList, setShowCreateList] = useState(false);
  const [listName, setListName] = useState("");
  const [err, setErr] = useState("");
  const [selectedListId, setSelectedListId] = useState("");
  const [showList, setShowList] = useState(false);
  const [showTooltip, setTooltip] = useState(false);
  const [showAddTip, setShowAddTip] = useState(false);
  const [loader, setLoader] = useState(false);
  const [newListLoader, setNewListLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState(accountsState.accountLists || []);

  useEffect(() => {
    if (trigger > 0) {
      if (!loading && accountIds?.length > 0) {
        setShowList(true);
      }
    }
  }, [trigger]);

  const resetToInitial = () => {
    setShowList(false);
    setSearchText("");
    setSelectedListId("");
    setListName("");
    setErr("");
  };

  const closePopup = () => {
    !loader && !newListLoader && resetToInitial();
  };

  const changeListNameAction = (id = "", value = "") => {
    err && setErr("");
    setListName(value);
    setSearchText("");
    setSelectedListId("");
  };

  const handleSearchText = (id, value) => {
    setSearchText(value);
    setSelectedListId("");
    setListName("");
    err && setErr("");
    const all = accountsState.accountLists || [];
    if (value) {
      setOptions(
        all.filter(item =>
          (item?.name?.toLowerCase()?.trim() || "").includes(
            value?.toLowerCase()
          )
        )
      );
    } else {
      setOptions(all);
    }
  };

  const searchInputObj = {
    name: "searchAccountListInput",
    id: "searchAccountListInput",
    type: "text",
    value: searchText,
    change: handleSearchText,
    placeHolder: "Search lists",
    focus: true
  };

  const handleChange = (id = "") => {
    setSelectedListId(id);
    setShowCreateList(false);
    setListName("");
    err && setErr("");
  };

  const addToListBtn = {
    id: "addAccountToSelectedListBtn",
    name: "addAccountToSelectedList",
    btnText: "Add to list",
    type: "button",
    loader,
    className: `addContactToSelectedListBtn ${
      selectedListId ? "" : "disabled"
    } ${loader ? "loading" : ""}`,
    click: () => {
      if (!selectedListId) return;
      setLoader(true);
      onAddToList(selectedListId, () => {
        setLoader(false);
        closePopup();
      });
    }
  };

  const createNewListAction = async () => {
    if (!newListLoader && listName?.trim()?.length) {
      try {
        setNewListLoader(true);
        const response = await accountsState.createAccountList(
          listName.trim()
        );
        if (response?.data?.error) {
          setErr("List name already exists.");
        } else if (response?.data?.id) {
          await accountsState.fetchAccountLists();
          setShowCreateList(false);
          setListName("");
          setSelectedListId(response.data.id);
          toasterState.setToastMsg("List created successfully", "success");
        } else {
          toasterState.setToastMsg(
            "Failed to create list. Please try again later",
            "failure"
          );
        }
        setNewListLoader(false);
      } catch (e) {
        setNewListLoader(false);
        console.error(e);
      }
    }
  };

  useEffect(() => {
    return () => {
      resetToInitial();
    };
  }, []);

  useEffect(() => {
    setOptions(accountsState.accountLists || []);
  }, [accountsState.accountLists]);

  const renderButton = () => {
    return (
      <div
        className={`addContactsToList ${
          accountIds?.length > 0 ? "enabled" : ""
        }`}
        onMouseEnter={() => setTooltip(true)}
        onMouseLeave={() => setTooltip(false)}
        onClick={() => {
          if (!loading && accountIds?.length > 0) {
            setShowList(true);
          }
        }}
      >
        <i className="material-icons">playlist_add</i>
        {showTooltip && (
          <CustomTooltip
            text={`${
              accountIds?.length > 0
                ? "Add to List"
                : "Select one or more accounts to add to list."
            }`}
            className={`${accountIds?.length > 0 ? "" : "bulkAddToList"}`}
          />
        )}
      </div>
    );
  };

  return (
    <div className="wrapAddTolistBtnPopup">
      {!hidden && renderButton()}
      {showList && (
        <div className="addListPopup">
          <div className="card animated customzoomIn">
            <div className="cardHeader">
              <div className="title">Add to List</div>
              <i className="material-icons close" onClick={closePopup}>
                close
              </i>
            </div>
            <div
              className={`cardBody ${newListLoader || loader ? "loading" : ""}`}
            >
              <CreateNewList
                showCreateList={showCreateList}
                setShowCreateList={setShowCreateList}
                listName={listName}
                err={err}
                loader={newListLoader}
                setListName={setListName}
                setErr={setErr}
                changeAction={changeListNameAction}
                setLoader={setNewListLoader}
                action={createNewListAction}
              />
              {(accountsState?.accountLists?.length > 0 && (
                <div className="selectListDropdown">
                  <div className="listHeading">Select a list</div>
                  <div className="selectOptions">
                    <div className="searchInputBox">
                      <i className="material-icons search">search</i>
                      <InputTextBox {...searchInputObj} />
                    </div>
                    <div className="optionList">
                      {options?.length > 0 ? (
                        options?.map((item, index) => {
                          const { count = 0, createTs = "", name = "", id = "" } =
                            item || {};
                          return (
                            <div
                              className={`options ${
                                selectedListId === id ? "disableSelect" : ""
                              }`}
                              key={`accountListId_${id}${index}`}
                            >
                              <div className="radioBtnCont">
                                <div className="radioButtonCont">
                                  <input
                                    type="radio"
                                    name="addToAccountListRadioCheckbox"
                                    id={id}
                                    onChange={() => handleChange(id)}
                                    checked={selectedListId === id}
                                    className="radioBtn"
                                  />
                                  <span className="contentClass" />
                                </div>
                              </div>
                              <label className="listDetails" htmlFor={id}>
                                <div
                                  className={`listInfo ${
                                    !createTs ? "alignSelfCenter" : ""
                                  }`}
                                >
                                  <span className="name">{name}</span>
                                  {createTs && (
                                    <span className="createdInfo">
                                      Created on {Utils.formatDate(createTs)}
                                    </span>
                                  )}
                                </div>
                                <div className="listContactsCount">
                                  <span className="count">{count}</span>
                                  <span className="text">
                                    Account{count > 1 ? "s" : ""}
                                  </span>
                                </div>
                              </label>
                            </div>
                          );
                        })
                      ) : (
                        <div className="noListFound">No list found</div>
                      )}
                    </div>
                  </div>
                </div>
              )) ||
                ""}
              <div
                className="addToListBtnWrap"
                onMouseEnter={() => setShowAddTip(true)}
                onMouseLeave={() => setShowAddTip(false)}
              >
                <Button {...addToListBtn} />
                {showAddTip && !loader && (
                  <CustomTooltip
                    className="addToListTip"
                    text={
                      selectedListId
                        ? `Add ${accountIds?.length || 0} account${
                            accountIds?.length === 1 ? "" : "s"
                          } to "${
                            accountsState.accountLists?.find(
                              l => l?.id === selectedListId
                            )?.name || "the selected list"
                          }"`
                        : "Select a list, or create one, to add the selected accounts"
                    }
                  />
                )}
              </div>
            </div>
          </div>
          <div className="bgOverlay" />
        </div>
      )}
    </div>
  );
});

export default AddAccountsToList;
